import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"
import _imports_0 from '../assets/shopping-cart-illustration.png'


const _withScopeId = n => (_pushScopeId("data-v-c0e7d356"),n=n(),_popScopeId(),n)
const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "d-print-none p-0 mb-1 mb-md-3 d-flex flex-column flex-md-row bg-white rounded-xl" }
const _hoisted_3 = { class: "text-left mt-md-5 mt-4 justify-content-center d-flex" }
const _hoisted_4 = ["alt"]
const _hoisted_5 = { class: "pt-md-5 pt-4 pr-5 pb-2 pl-5 pb-md-5 align-self-start col-md-7" }

import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import GovHubTopNav from './GovHubTopNav.vue'
import GovHubFooter from './Footer.vue'
import IntroModal from './IntroModal.vue'
import Announcements from './Announcements.vue'
import RedirectWarningModal from '@grantstreet/psc-vue/components/RedirectWarningModal.vue'
import LoginRequiredModal from './LoginRequiredModal.vue'
import CartIconPopper from '@grantstreet/cart-vue/src/components/CartIconPopper.vue'
import AppImageLayout from './AppImageLayout.vue'
import ChatBot from '../views/ChatBot.vue'
import EventBus from '@grantstreet/psc-vue/utils/event-bus.ts'
import { configState, configGetters } from '@grantstreet/psc-config'
import { useScrollAmount } from '@grantstreet/psc-vue/composables/scroll-tracker.ts'
import { useGsgUser } from '@grantstreet/user'
import { useI18n } from 'vue-i18n'
import { useLoginHelpers } from '../login-helpers.js'


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const store = useStore()
const route = useRoute()
const i18n = useI18n()
const { user } = useGsgUser()
const { handleLogin } = useLoginHelpers()
const topNavActive = ref(false)
const redirectLink = ref<string>()
const loginRequiredReason = ref('')
const useChatBot = computed(() => configGetters.useChatBot)
const payableSources = computed(() => configGetters.payableSources)
const config = computed(() => configState.config)
const count = computed(() => store.getters['Cart/count'])
const locale = computed(() => store.getters['PayHub/locale'])
const enableHeader = computed(() => store.state.PayHub?.enableHeader)
const enableFooter = computed(() => store.state.PayHub?.enableFooter)
const showAnnouncements = computed(() => store.state.PayHub?.showAnnouncements)
const enableFloatingCart = computed(() => store.state.PayHub?.enableFloatingCart)
const clientTitle = computed(() => store.getters['PayHub/clientTitle'])
const favicon = computed(() => store.getters['PayHub/favicon'])
const departmentTitle = computed(() => {
  for (const source of payableSources.value) {
    if (source.pageRoute === route.meta.route && source.departmentDisplayName) {
      return source.departmentDisplayName[locale.value]
    }
  }

  // We don't want the original department to appear on the
  // confirmation, checkout, or receipt pages
  return ''
})

const profileInfo = computed(() => user.loggedIn ? {
  givenName: user.givenName,
  familyName: user.familyName,
  email: user.email,
  phone: user.phone,
} : null)
const showGSGBranding = computed(() => config.value?.payHub?.showGSGBranding)
const colorTheme = computed(() => config.value?.payHub?.colorTheme || 'default')

const scrollAmount = useScrollAmount()
const normalizedScrollDistance = computed(() => Math.max(Math.floor(scrollAmount.value), 0))
const hasRedirectLink = computed({
  get: () => Boolean(redirectLink.value),
  set: value => {
    // value is a computed boolean, if value is set to true, retain current
    // raw value, otherwise set to null
    if (value) {
      return
    }

    redirectLink.value = undefined
  },
})

useHead({
  titleTemplate: () => clientTitle.value ? clientTitle.value + ' - ' + i18n.t('payments_and_services') : i18n.t('payments_and_services'),
  meta: [
    {
      name: 'description',
      content: `Pay${clientTitle.value ? ' your ' + clientTitle.value : ''} bills online in a few simple clicks. Manage and make payments in one convenient platform anytime, anywhere.`,
    },
  ],
  link: [
    {
      rel: 'icon',
      href: favicon.value || '/govhub.ico',
    },
  ],
})
const overrideTheme = () => {
  if (colorTheme.value !== 'default') {
    const link = document.createElement('link')
    link.id = 'override-theme'
    link.rel = 'stylesheet'
    link.type = 'text/css'
    link.href = '/web/widgets/current/themes/' + colorTheme.value + '.css'
    link.media = 'all'
    document.getElementsByTagName('head')[0].appendChild(link)
  }
}

onMounted(() => {
  EventBus.$on('loginRequired', (why) => {
    loginRequiredReason.value = why
  })
  // Note if the config changes, we don't bother to re-override
  // One flash of color is enough
  overrideTheme()
})


return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["container-fluid p-0", [
      `scrolled-${normalizedScrollDistance.value}`,
      ...[_ctx.$route.name ? `page-${String(_ctx.$route.name).toLowerCase()}` : ''],
    ]])
  }, [
    (!_ctx.$route.meta.hideHeader && enableHeader.value)
      ? (_openBlock(), _createElementBlock("header", _hoisted_1, [
          _createVNode(GovHubTopNav, {
            modelValue: topNavActive.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((topNavActive).value = $event)),
            onRedirectClick: _cache[1] || (_cache[1] = ($event: any) => (redirectLink.value = $event))
          }, null, 8, ["modelValue"]),
          _createVNode(RedirectWarningModal, {
            modelValue: hasRedirectLink.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((hasRedirectLink).value = $event)),
            "redirect-link": redirectLink.value,
            "login-site-enabled": Boolean(_unref(configState).config.login?.meta?.enabled),
            onLogIn: _cache[3] || (_cache[3] = ($event: any) => (_unref(handleLogin)($event)))
          }, null, 8, ["modelValue", "redirect-link", "login-site-enabled"]),
          (showAnnouncements.value)
            ? (_openBlock(), _createBlock(Announcements, {
                key: 0,
                class: "w-100"
              }))
            : _createCommentVNode("", true),
          _createVNode(IntroModal),
          (useChatBot.value)
            ? (_openBlock(), _createBlock(ChatBot, {
                key: 1,
                "open-cart-icon-popper": !_ctx.$route.meta.hideCartIcon && enableFloatingCart.value && count.value > 0
              }, null, 8, ["open-cart-icon-popper"]))
            : _createCommentVNode("", true),
          _createVNode(_Transition, { name: "bounceRight" }, {
            default: _withCtx(() => [
              _withDirectives(_createVNode(CartIconPopper, {
                id: "floating-cart",
                placement: "top",
                class: "text-white",
                "transition-type": "bump-badge",
                big: "",
                "data-test": "mini-cart-icon",
                onContinue: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$router.push({
            name: 'checkout',
            params: _ctx.$route.params,
          })))
              }, null, 512), [
                [_vShow, !_ctx.$route.meta.hideCartIcon && enableFloatingCart.value && count.value > 0]
              ])
            ], undefined, true),
            _: 1
          })
        ]))
      : _createCommentVNode("", true),
    _createVNode(LoginRequiredModal, {
      show: Boolean(loginRequiredReason.value),
      closable: true,
      onClosed: _cache[5] || (_cache[5] = ($event: any) => (loginRequiredReason.value = ''))
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("img", {
              class: "shopping-cart-image",
              src: _imports_0,
              alt: _ctx.$t('screenshot.desc')
            }, null, 8, _hoisted_4)
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", null, _toDisplayString(loginRequiredReason.value), 1),
            _createElementVNode("h2", null, _toDisplayString(_ctx.$t('receipt.signup.header')), 1),
            _createElementVNode("div", null, _toDisplayString(_ctx.$t('receipt.signup.desc')), 1)
          ])
        ])
      ], undefined, true),
      _: 1
    }, 8, ["show"]),
    _createVNode(AppImageLayout, { class: "main-content" }, {
      default: _withCtx(() => [
        (_openBlock(), _createBlock(_component_router_view, {
          key: _ctx.$route.path,
          role: "main",
          "aria-label": departmentTitle.value || (_ctx.$route.meta.title ? _ctx.$route.meta.title[_ctx.$i18n.locale] : null),
          class: _normalizeClass([{
          'gh-container': !_ctx.$route.meta.fullWidth && !_ctx.$route.meta.narrowPage,
          'max-width-md': _ctx.$route.meta.narrowPage,
          'mx-auto': _ctx.$route.meta.narrowPage,
        }, "page-content"]),
          "data-test": `page-${String(_ctx.$route.name)}`,
          onRedirectClick: _cache[6] || (_cache[6] = ($event: any) => (redirectLink.value = $event))
        }, null, 8, ["aria-label", "class", "data-test"])),
        (!_ctx.$route.meta.hideFooter && enableFooter.value)
          ? (_openBlock(), _createBlock(GovHubFooter, {
              key: 0,
              "default-contact-us-info": profileInfo.value,
              "show-gsg-branding": showGSGBranding.value,
              class: _normalizeClass({
          'gh-container': !_ctx.$route.meta.fullWidthFooter,
          'max-width-md': _ctx.$route.meta.narrowPage,
          'mx-auto': _ctx.$route.meta.narrowPage,
        }),
              "footer-bar-class": {
          'gh-page-margin': !_ctx.$route.meta.fullWidthFooter,
        }
            }, null, 8, ["default-contact-us-info", "show-gsg-branding", "class", "footer-bar-class"]))
          : _createCommentVNode("", true)
      ], undefined, true),
      _: 1
    })
  ], 2))
}
}

})